import { IconNames, Modal, ModalFoot, ModalHead } from '@dixa/lib-shared-habla';
import { useCallback } from 'react';

import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { elevioActions } from '../../../elevio.actions';
import { elevioSelectors } from '../../../elevio.reducer/selectors';
import { useKcsSettings, useResolutionModalFooterProps } from '../../../hooks';
import { getUrl } from '../../../utils';

import { ResolutionModalBody } from './index';

export function ResolutionModal(): JSX.Element | null {
  const stage = useAppSelector(elevioSelectors.resolution.stage);
  const dispatch = useAppDispatch();

  const { kcsSettingsData } = useKcsSettings();
  const conversationId = useAppSelector(elevioSelectors.conversation.id);
  const languageId = useAppSelector(
    elevioSelectors.suggestion.selectedLanguageId,
  );

  const isCancelStage = stage === 'cancelWarning';

  const cancel = useCallback(() => {
    if (isCancelStage && conversationId) {
      return dispatch(
        elevioActions.resolution.ticketResolutionExited({
          ticketId: conversationId.toString(),
          url: getUrl(),
          languageId,
        }),
      );
    }
    return dispatch(elevioActions.resolution.setCancelWarning());
  }, [isCancelStage, conversationId, dispatch, languageId]);

  if (!conversationId) return null;

  const secondaryButton = kcsSettingsData?.kcsSettings.showCloseButton
    ? {
        icon: 'close' as IconNames,
        onClick: cancel,
        tooltipContent: 'Close',
      }
    : undefined;

  const modalHeadTitle = isCancelStage
    ? 'Resolve Conversation Without Solution?'
    : `Resolve Conversation`;

  const isOpen =
    stage === 'prompt' ||
    stage === 'solution' ||
    stage === 'solutionDropdownOpen' ||
    stage === 'solutionArticleSelected' ||
    stage === 'cancelWarning' ||
    stage === 'noSolution' ||
    stage === 'noSolutionNoDraftArticle' ||
    stage === 'noSolutionDraftArticle';

  if (!conversationId) return null;

  return (
    <Modal isOpen={isOpen} onClose={cancel}>
      <ModalHead title={modalHeadTitle} secondaryButton={secondaryButton} />
      <ResolutionModalBody />
      <ResolutionModalFooter />
    </Modal>
  );
}

/** Resolution modal footer */
function ResolutionModalFooter() {
  const {
    confirmLabel,
    confirmOnClickHandler,
    disabled,
    cancelOnClickHandler,
    cancelLabel,
  } = useResolutionModalFooterProps();

  return (
    <ModalFoot
      confirmName={confirmLabel}
      confirmOnClick={confirmOnClickHandler}
      confirmProps={{ disabled }}
      cancelOnClick={cancelOnClickHandler}
      cancelName={cancelLabel}
    />
  );
}
